import { useCallback, useEffect, useState } from 'react';
import { Loader2, Presentation } from 'lucide-react';
import api from '../../lib/api';
import { StoryboardSlidesPanel } from './StoryboardSlidesPanel';
import { type ExternalDocument } from '../../lib/googleWorkspace';
import { Card, CardContent } from '../ui/Card';

interface KolStoryboardViewerProps {
  jobId: string;
  invitationId: string;
}

/** In-app storyboard viewer for KOLs — no Google account required. */
export function KolStoryboardViewer({ jobId, invitationId }: KolStoryboardViewerProps) {
  const [storyboard, setStoryboard] = useState<ExternalDocument | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ documents: ExternalDocument[] }>(
        `/google-workspace/jobs/${jobId}/documents`
      );
      const doc = (res.data.documents ?? []).find(
        (d) => d.docType === 'presentation' && d.invitationId === invitationId
      );
      setStoryboard(doc ?? null);
    } catch {
      setStoryboard(null);
    } finally {
      setLoading(false);
    }
  }, [jobId, invitationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Card className="border-purple-200/50">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  if (!storyboard) {
    return (
      <Card className="border-purple-200/40 bg-purple-50/10">
        <CardContent className="py-6 text-center text-sm text-gray-600">
          <Presentation className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          No storyboard linked yet. Your agency will share slides here when ready.
        </CardContent>
      </Card>
    );
  }

  return (
    <StoryboardSlidesPanel
      jobId={jobId}
      documentId={storyboard.id}
      readOnly
      kolViewer
    />
  );
}
